import { operations, paths } from "./generated-types";
import createClient from "openapi-fetch";
import { rolesStorageKey, userIdStorageKey } from "../UserData.tsx";

/**
 * Die API wird Same-Origin unter /api ausgeliefert: in Produktion routet Caddy
 * `handle_path /api*` auf das Backend, lokal übernimmt das der Dev-Proxy in
 * vite.config.ts. Beide entfernen das Präfix, bevor sie weiterleiten.
 *
 * Dadurch ist das gebaute Bundle unabhängig von der Umgebung — es muss keine
 * absolute Backend-URL zur Build-Zeit eingebacken werden.
 */
const API_BASE_PATH = "/api";

const client = createClient<paths>({
  baseUrl: API_BASE_PATH,
  credentials: "include",
});

/**
 * Mit einer Token-Laufzeit von zwölf Stunden trifft ein abgelaufenes Token
 * regelmäßig eine offene Sitzung. Ohne Behandlung hält die UI den Nutzer
 * weiter für eingeloggt und zeigt leere oder fehlerhafte Seiten. Der
 * localStorage wird geräumt und die Seite auf /login geschickt.
 *
 * Ausgenommen sind die Endpunkte, bei denen ein 401 die fachliche Antwort ist:
 * der Login selbst, und user.read/self beim ersten Aufruf ohne Sitzung.
 */
const pathsWithExpected401 = ["/v1/user.write/login", "/v1/user.read/self"];

client.use({
  onResponse({ response, request }) {
    if (response.status !== 401) {
      return;
    }

    const path = new URL(request.url).pathname.replace(API_BASE_PATH, "");

    if (pathsWithExpected401.includes(path)) {
      return;
    }

    window.localStorage.removeItem(userIdStorageKey);
    window.localStorage.removeItem(rolesStorageKey);
    window.location.assign("/login");
  },
});

export const register = (
  body: operations["candidate.write.register"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/candidate.write/register", { body });
};

export const login = (
  body: operations["user.write.login"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/user.write/login", { body });
};

export const getSelf = (
  query: operations["user.read.self"]["parameters"]["query"],
) => {
  return client.GET("/v1/user.read/self", { params: { query } });
};

export const listApplicationProcessesOpenForEnrolment = (
  query: operations["applicationprocess.read.listopenforenrolment"]["parameters"]["query"],
) => {
  return client.GET("/v1/applicationprocess.read/listopenforenrolment", {
    params: { query },
  });
};

export const getCandidate = (
  query: operations["candidate.read.get"]["parameters"]["query"],
) => {
  return client.GET("/v1/candidate.read/get", {
    params: { query },
  });
};

export const logout = () => {
  return client.POST("/v1/user.write/logout");
};

export const verifyEmail = (
  body: operations["user.write.verifyemail"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/user.write/verifyemail", { body });
};

export const requestPasswordReset = (
  body: operations["user.write.requestpasswordreset"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/user.write/requestpasswordreset", { body });
};

export const resetPassword = (
  body: operations["user.write.resetpassword"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/user.write/resetpassword", { body });
};

export const listCandidates = (
  query: operations["candidate.read.list"]["parameters"]["query"],
) => {
  return client.GET("/v1/candidate.read/list", {
    params: { query },
  });
};

export const enrolToApplicationProcess = (
  body: operations["participationinapplicationprocess.write.enrol"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/participationinapplicationprocess.write/enrol", {
    body,
  });
};

export const listParticipationInApplicationProcessByApplicationProcessIdAndFieldOfStudyId =
  (
    query: operations["participationinapplicationprocess.read.listbyapplicationprocessidandfieldofstudyid"]["parameters"]["query"],
  ) => {
    return client.GET(
      "/v1/participationinapplicationprocess.read/listbyapplicationprocessidandfieldofstudyid",
      {
        params: { query },
      },
    );
  };

export const listParticipationInApplicationProcessByCandidateId = (
  query: operations["participationinapplicationprocess.read.listbycandidateid"]["parameters"]["query"],
) => {
  return client.GET(
    "/v1/participationinapplicationprocess.read/listbycandidateid",
    {
      params: { query },
    },
  );
};

export const getApplicationProcess = (
  query: operations["applicationprocess.read.get"]["parameters"]["query"],
) => {
  return client.GET("/v1/applicationprocess.read/get", {
    params: { query },
  });
};

export const listAllApplicationProcesses = () => {
  return client.GET("/v1/applicationprocess.read/listall");
};

export const listApplicationProcessesByCommitteeMember = (
  query: operations["applicationprocess.read.listbycommitteemember"]["parameters"]["query"],
) => {
  return client.GET("/v1/applicationprocess.read/listbycommitteemember", {
    params: { query },
  });
};

export const getDocumentUploadLink = (
  query: operations["participationinapplicationprocess.read.getdocumentuploadlink"]["parameters"]["query"],
) => {
  return client.GET(
    "/v1/participationinapplicationprocess.read/getdocumentuploadlink",
    {
      params: { query },
    },
  );
};

export const getHomeworkUploadLink = (
  query: operations["participationinapplicationprocess.read.gethomeworkuploadlink"]["parameters"]["query"],
) => {
  return client.GET(
    "/v1/participationinapplicationprocess.read/gethomeworkuploadlink",
    {
      params: { query },
    },
  );
};

export const getEntranceExamUploadLink = (
  query: operations["participationinapplicationprocess.read.getentranceexamuploadlink"]["parameters"]["query"],
) => {
  return client.GET(
    "/v1/participationinapplicationprocess.read/getentranceexamuploadlink",
    {
      params: { query },
    },
  );
};

export const setEntranceExamResult = (
  body: operations["participationinapplicationprocess.write.setentranceexamresult"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST(
    "/v1/participationinapplicationprocess.write/setentranceexamresult",
    {
      body,
    },
  );
};

export const setWorkSampleAsAnalogoue = (
  body: operations["participationinapplicationprocess.write.setworksampleasanalogoue"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST(
    "/v1/participationinapplicationprocess.write/setWorkSampleAsAnalogoue",
    {
      body,
    },
  );
};

export const setEntranceExamAssessment = (
  body: operations["participationinapplicationprocess.write.setentranceexamassessment"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST(
    "/v1/participationinapplicationprocess.write/setentranceexamassessment",
    {
      body,
    },
  );
};

export const createApplicationProcess = (
  body: operations["applicationprocess.write.create"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/applicationprocess.write/create", { body });
};

export const updateApplicationProcess = (
  body: operations["applicationprocess.write.update"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/applicationprocess.write/update", { body });
};

export const listCommitteeMembers = () => {
  return client.GET("/v1/committeemember.read/list");
};

export const inviteCommitteeMember = (
  body: operations["committeemember.write.invite"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/committeemember.write/invite", { body });
};

export const updateCommitteeMember = (
  body: operations["committeemember.write.update"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/committeemember.write/update", { body });
};

export const reinviteCommitteeMember = (
  body: operations["committeemember.write.reinvite"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/committeemember.write/reinvite", { body });
};

export const setCommitteeMemberDeactivated = (
  body: operations["committeemember.write.setdeactivated"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/committeemember.write/setdeactivated", { body });
};

export const acceptInvitation = (
  body: operations["user.write.acceptinvitation"]["requestBody"]["content"]["application/json"],
) => {
  return client.POST("/v1/user.write/acceptinvitation", { body });
};

export const getPublicConfig = async () => {
  return client.GET("/v1/config.read/get");
};

export const csvDownloadPath = (applicationProcessId: string) =>
  `${API_BASE_PATH}/v1/participationinapplicationprocess.read/export.xlsx?applicationProcessId=${applicationProcessId}`;

/*
  Downloads laufen ueber einen eigenen Endpunkt, der beim Klick auf eine frisch
  signierte URL weiterleitet — statt wie frueher eine Stunde gueltige Signaturen
  schon beim Laden der Liste mitzuliefern. Diese Links laufen deshalb nie ab,
  und die Liste muss keine Signaturen mehr erzeugen.

  Der Pfad wird hier gebaut statt vom Server geliefert: der Browser schickt den
  Cookie ohnehin mit, und so kennt nur der Client seinen eigenen API-Prefix.
*/
export const documentDownloadPath = (
  participationInApplicationProcessId: string,
  requiredDocumentId: string,
) =>
  `${API_BASE_PATH}/v1/participationinapplicationprocess.read/downloaddocument?participationInApplicationProcessId=${participationInApplicationProcessId}&requiredDocumentId=${requiredDocumentId}`;

export const homeworkDownloadPath = (
  participationInApplicationProcessId: string,
) =>
  `${API_BASE_PATH}/v1/participationinapplicationprocess.read/downloadhomework?participationInApplicationProcessId=${participationInApplicationProcessId}`;

export const entranceExamDownloadPath = (
  participationInApplicationProcessId: string,
) =>
  `${API_BASE_PATH}/v1/participationinapplicationprocess.read/downloadentranceexam?participationInApplicationProcessId=${participationInApplicationProcessId}`;
